'use client';

import { CauseBySourceRefQuery, CauseBySourceRefQueryVariables } from '../../lib/graphql';
import { formatCurrencySymbol, getCurrencyFlag } from '../../utils/currency';

import { AcceptedCurrencyCode } from "../../lib/graphql";
import { Alert } from '../Alert/Alert';
import { LoadingIndicator } from '@justgiving/page-components';
import { Modal } from '../Modal/Modal';
import { Stack } from '../Stack/Stack';
import classNames from 'classnames';
import clientConfig from '../../client/config';
import styles from './CheckoutModal.module.scss';
import { useAuth } from '../Providers/AuthProvider';
import { usePage } from '../Providers/PageProvider';
import { useQuery } from '../../hooks/useQuery';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const query = /* GraphQL */ `
  query CauseBySourceRef($sourceRef: String!) {
    cause(input: {sourceRef: $sourceRef}) {
      cause {
        type
        sourceRef
        causeRef
        settlementCurrency
        paymentMethods {
          currency
        }
      }
      error
    }
  }
`;

type CheckoutModalProps = {
  isOpen: boolean,
  setIsOpen: (isOpen: boolean) => void,
  isPrimaryBrandColorLight: boolean
}

export const CheckoutModal: React.FC<CheckoutModalProps> = (
  {
    isOpen,
    setIsOpen,
    isPrimaryBrandColorLight
  }: CheckoutModalProps
  ) => {
  const { id } = usePage();
  const { t } = useTranslation();
  const auth = useAuth();

  const { data, loading, errors} = useQuery<
    CauseBySourceRefQuery,
    CauseBySourceRefQueryVariables
  >(query, {
    withAuth: true,
    variables: {
      sourceRef: id,
    },
  });

  const cause = data?.cause?.cause;
  const settlementCurrencyWithFallback = cause?.settlementCurrency ?? 'GBP';

  const [isMonthly, setIsMonthly] = useState(false);
  const [currency, setCurrency] = useState<AcceptedCurrencyCode>(settlementCurrencyWithFallback);
  const [isAmountValid, setIsAmountValid] = useState(true);
  const [hasAmountBlurred, setHasAmountBlurred] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  const shouldDisplayFrequency = !auth?.isGuest()
  // TODO: Remove 'shouldDisplayHideAmountInput' logic when "hide amount" is supported for Direct Debits
  const shouldDisplayHideAmountInput = !(isMonthly && settlementCurrencyWithFallback === "GBP");

  const showLoader = Boolean(loading);
  const showError = Boolean(errors || data?.cause?.error);

  const validateAmount = (amountString: string): boolean => {
    setHasAmountBlurred(true);
    if (!amountString) {
      setIsAmountValid(false);
      return false;
    }
    if (!/^\d+([.]\d{1,2})?$/.test(amountString)) {
      setIsAmountValid(false);
      return false;
    }
    const amountNumeric = parseFloat(amountString);
    if (!amountNumeric) {
      setIsAmountValid(false);
      return false;
    }
    if (amountNumeric < 2) {
      setIsAmountValid(false);
      return false;
    }
    if (amountNumeric > 100000) {
      setIsAmountValid(false);
      return false;
    }
    setIsAmountValid(true);
    return true;
  }

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    const formData = new FormData(event.currentTarget);
    const isRecurring = formData.get('isRecurring') === 'true';
    const amount = formData.get(isRecurring ? 'amount' : 'donationValue')
    
    if (!validateAmount(amount as string)) {
      event.preventDefault();
    } else {
      setIsNavigating(true);
    }
  }

  return (
    <Modal
      open={isOpen}
      onClose={() => setIsOpen(false)}
      className={styles.modalWrapper}
      ariaLabel={t('checkoutModal.label') ?? undefined}
    >
      <Modal.CloseButton className={styles.closeButton} />
      <Modal.Content padding='sizeSpacing04' >
        <Modal.Body>
          <Stack justifyContent={showLoader ? "center" : "flex-start"} className={styles.contentWrapper}>
            {showError && (
              <div className='mt-6'>
                <Alert>{t('checkoutModal.error')}</Alert>
              </div>
            )}
            {showLoader && <LoadingIndicator size="large" color="brand" />}
            {cause &&
              <>
                <h1 className='text-center cp-heading-large mb-0'>{t('checkoutModal.heading')}</h1>
                <form 
                  noValidate
                  method='GET'
                  action={`${clientConfig.linkServiceUrl}/${cause?.sourceRef}`}
                  onSubmit={onSubmit}
                >
                  {shouldDisplayFrequency &&
                    <DonationFrequency
                      isMonthly={isMonthly}
                      setIsMonthly={setIsMonthly}
                    />
                  }
                  <DonationAmount
                    paymentMethods={cause.paymentMethods}
                    currency={currency}
                    setCurrency={setCurrency}
                    wrapperClassName={shouldDisplayFrequency ? 'mt-4' : 'mt-5'}
                    hasAmountBlurred={hasAmountBlurred}
                    setHasAmountBlurred={setHasAmountBlurred}
                    validateAmount={validateAmount}
                    isAmountValid={isAmountValid}
                    isMonthly={isMonthly}
                  />
                  {shouldDisplayHideAmountInput && <HideAmount />}
                  <input
                    type="hidden"
                    name="startedBy"
                    value="webjustgiving"
                  />
                  <ContinueButton
                    isAmountValid={isAmountValid}
                    isNavigating={isNavigating}
                    isPrimaryBrandColorLight={isPrimaryBrandColorLight}
                  />
                </form>
              </>
            }
          </Stack>
        </Modal.Body>
      </Modal.Content>
    </Modal>
  );
};

type DonationFrequencyProps = {
  isMonthly: boolean;
  setIsMonthly: (newValue: boolean) => void;
}

const DonationFrequency: React.FC<DonationFrequencyProps> = ({isMonthly, setIsMonthly}) => {
  const { t } = useTranslation();
  return (
    <div className={styles.radioButtonGroup} role="group" aria-label={t('checkoutModal.frequency.groupName') ?? undefined}>
      <label
        className={classNames('cp-body-large', styles.radioButton, {
          [styles.radioButtonActive]: !isMonthly,
        })}
        htmlFor='recurrence-single'
      >
        <input
          type="radio"
          id='recurrence-single'
          className="cp-sr-only-always"
          onChange={(event)=> {setIsMonthly(event.target.id === 'recurrence-monthly')}}
          checked={!isMonthly}
          name='isRecurring'
          value="false"
        />
        {t('checkoutModal.frequency.oneOff')}
      </label>
      <label
        className={classNames('cp-body-large', styles.radioButton, {
          [styles.radioButtonActive]: isMonthly,
        })}
        htmlFor='recurrence-monthly'
      >
        <input
          type="radio"
          id='recurrence-monthly'
          className="cp-sr-only-always"
          onChange={(event)=> {setIsMonthly(event.target.id === 'recurrence-monthly')}}
          checked={isMonthly}
          name='isRecurring'
          value="true"
        />
        {t('checkoutModal.frequency.monthly')}
      </label>
    </div>
  )
}

type CurrencySelectProps = {
  paymentMethods: {currency: string}[];
  currency: AcceptedCurrencyCode;
  setCurrency: (newValue: AcceptedCurrencyCode) => void;
}

const CurrencySelect: React.FC<CurrencySelectProps> = ({paymentMethods, currency, setCurrency}) => {
  const { t } = useTranslation();
  
  const supportedCurrencyCodeList: AcceptedCurrencyCode[] = paymentMethods.map(paymentMethod => paymentMethod.currency as AcceptedCurrencyCode)
  const currencySelectList = supportedCurrencyCodeList.map(currency => (
      <option key={`currency-${currency}`} value={currency}>
        {getCurrencyFlag(currency)} {currency} - {t(`checkoutModal.currency.${currency}`)}
      </option>
    ));

  return (
    <>
      <div className={styles.currencyWrapper}>
        <div className={classNames('cp-font-bold', 'text-center', styles.currency)}>
          <span
            className={styles.currencySymbol}
            aria-hidden="true"
          >
            {formatCurrencySymbol(currency)}
          </span>
          <span aria-hidden="true">
            {currency}{' '}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              xmlSpace="preserve"
              width={9}
              height={11}
              viewBox="0 0 255 255"
              className={styles.currencyArrow}
            >
              <path d="m0 63.75 127.5 127.5L255 63.75z" />
            </svg>
          </span>
          <select
            name="currency"
            className={styles.currencySelect}
            aria-label={t('checkoutModal.currency.label') ?? undefined}
            value={currency}
            onChange={(event) => {setCurrency(event.target.value as AcceptedCurrencyCode)}}
          >
            {currencySelectList}
          </select>
        </div>
      </div>
      <div className={styles.divider}></div>
    </>
  )
}

type DonationAmountProps = CurrencySelectProps & {
  wrapperClassName: string;
  isAmountValid: boolean;
  hasAmountBlurred: boolean;
  setHasAmountBlurred: (newValue: boolean) => void;
  validateAmount: (amount: string) => void;
  isMonthly: boolean;
}

const DonationAmount: React.FC<DonationAmountProps> = ({paymentMethods, currency, setCurrency, wrapperClassName, hasAmountBlurred, setHasAmountBlurred, validateAmount, isAmountValid, isMonthly}) => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState('');
  
  const handleChangeAmount = (event: React.ChangeEvent<HTMLInputElement>)=> {
    setAmount(event.target.value)
    hasAmountBlurred && validateAmount(event.target.value)
  }

  const currencyName = t(`checkoutModal.currency.${currency}`);

  return (
    <>
      <div className={classNames(styles.amountWrapper, wrapperClassName, {[styles.isError]: !isAmountValid})}>
        <CurrencySelect
          paymentMethods={paymentMethods}
          currency={currency}
          setCurrency={setCurrency}
        />
        <div className={styles.amount}>
          <input
            name={isMonthly ? "amount" : "donationValue"}
            type="text" 
            inputMode="decimal"
            pattern="[0-9]*"
            autoComplete="off"
            aria-label={t('checkoutModal.donationAmount.label', { currencyName }) ?? undefined}
            className={classNames(styles.amountInput, {[styles.isError]: !isAmountValid})}
            aria-invalid={!isAmountValid}
            required
            value={amount}
            onChange={handleChangeAmount}
            onBlur={()=>{
              setHasAmountBlurred(true);
              validateAmount(amount);
            }}
          />
        </div>
      </div>
      {!isAmountValid &&
        <p className='cp-input-message-error mt-2 mb-0' role="alert">
          {t('checkoutModal.donationAmount.error')}
        </p>
      }
    </>
  )
}

const HideAmount: React.FC = () => {
  const { t } = useTranslation();
  const [shouldHideAmount, setShouldHideAmount] = useState(false);
  
  return (
    <div className='mt-4'>
      <input
        id='hideAmount'
        name='hideAmount'
        type="checkbox"
        className="cp-input-checkbox"
        checked={shouldHideAmount}
        value={shouldHideAmount.toString()}
        onChange={(event)=> {setShouldHideAmount(event.target.checked)}}
      />
      <label 
      className={classNames(styles.checkboxLabel, 'cp-label')}
      htmlFor='hideAmount'>
        {t('checkoutModal.hideDonationAmount.label')}
      </label>
    </div>
  )
}

type ContinueButtonProps = {
  isAmountValid: boolean;
  isNavigating: boolean;
  isPrimaryBrandColorLight: boolean;
}

const ContinueButton: React.FC<ContinueButtonProps> = ({isAmountValid, isNavigating, isPrimaryBrandColorLight}) => {
  const { t } = useTranslation();
  const isDisabled = !isAmountValid || isNavigating

  return (
    <button
      type="submit"
      disabled={isDisabled}
      aria-disabled={isDisabled}
      className={classNames(
        styles.continueButton,
        "cp-btn w-full mt-6",
        {
          ['cp-btn-primary branded-text-neutral']: !isPrimaryBrandColorLight,
          ['cp-btn-secondary']: isPrimaryBrandColorLight,
        },
      )}
    >
      {isNavigating
        ? <LoadingIndicator size="small" color={isPrimaryBrandColorLight ? "brand" : "white"} />
        : <>{t('checkoutModal.link.continue')}<span className='cp-sr-only'>{' '}{t('checkoutModal.link.toTheCheckout')}</span></>
      }
    </button>
  )
}
